/**
 * Trial Signup Page — /trial-signup
 * Self-service 1-day trial registration. Read-only access to all demo data.
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/auth';
import { ArrowRight, Eye, EyeOff, Sparkles, Clock, ShieldCheck, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BENEFITS = [
  { label: 'Instant access — no credit card', icon: Sparkles },
  { label: 'All demo data preloaded (suppliers, POs, contracts, claims)', icon: CheckCircle },
  { label: '24-hour read-only access (no admin actions)', icon: ShieldCheck },
  { label: 'Trial auto-expires; sales team will reach out', icon: Clock },
];

export default function TrialSignupPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { trialSignup } = useAuth();
  const navigate = useNavigate();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await trialSignup(form);
      toast.success('Trial started — welcome! You have 24 hours of full read access.');
      navigate('/app');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start your trial. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row" data-testid="trial-signup-page">
      {/* ─── Left: brand panel ─── */}
      <div className="lg:w-1/2 bg-[#0F131C] text-white p-10 lg:p-16 relative overflow-hidden flex flex-col justify-between">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-[#F26B1F]/15 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 -left-20 w-[300px] h-[300px] bg-white/5 rounded-full blur-[100px]" />

        <div className="relative">
          <Link to="/" className="inline-block" data-testid="trial-signup-logo-link">
            <img src="/procurex-logo-dark.svg" alt="ProcureX" className="h-11" />
          </Link>
        </div>

        <div className="relative">
          <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">1-day trial</p>
          <h1 className="text-3xl lg:text-4xl font-heading font-bold leading-tight tracking-tight">
            Walk the full source-to-settle lifecycle —
            <span className="italic font-light text-blue-200"> on your own time.</span>
          </h1>
          <p className="text-sm text-blue-100/80 mt-5 leading-relaxed max-w-md">
            Your trial unlocks the entire Coastal Energy Petrochemicals demo dataset — 8 suppliers,
            5 contracts, 12 POs, 4 active claims, and a ₹22.5L LD ticker. No sales call required.
          </p>

          <ul className="mt-8 space-y-3">
            {BENEFITS.map(({ label, icon: Icon }) => (
              <li key={label} className="flex items-start gap-3 text-sm text-blue-50">
                <Icon className="w-4 h-4 text-[#F26B1F] mt-0.5 flex-shrink-0" />
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative" />
      </div>

      {/* ─── Right: form ─── */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                    className="w-full max-w-md">
          <h2 className="text-2xl font-heading font-bold text-slate-900">Start your 1-day trial</h2>
          <p className="text-sm text-slate-500 mt-1.5">No credit card. Expires automatically after 24 hours.</p>

          <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="trial-signup-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600 font-medium mb-1.5 block">Full name *</label>
                <Input value={form.name} onChange={update('name')} required
                       placeholder="Jane Procurement" className="h-10"
                       data-testid="trial-name-input" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium mb-1.5 block">Company</label>
                <Input value={form.company} onChange={update('company')}
                       placeholder="Acme Corp" className="h-10"
                       data-testid="trial-company-input" />
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-600 font-medium mb-1.5 block">Work email *</label>
              <Input type="email" value={form.email} onChange={update('email')} required
                     placeholder="jane@acme.com" className="h-10"
                     data-testid="trial-email-input" />
            </div>

            <div>
              <label className="text-xs text-slate-600 font-medium mb-1.5 block">
                Password * <span className="text-slate-400 font-normal">(min 8 characters)</span>
              </label>
              <div className="relative">
                <Input type={showPass ? 'text' : 'password'} value={form.password} onChange={update('password')}
                       required minLength={8} placeholder="Choose a strong password"
                       className="h-10 pr-10" data-testid="trial-password-input" />
                <button type="button" onClick={() => setShowPass(!showPass)} aria-label="toggle password"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading}
                    className="w-full bg-[#F26B1F] hover:bg-[#D85A14] text-white h-11 font-medium"
                    data-testid="trial-submit-btn">
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Start my 1-day trial <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>

            <p className="text-[11px] text-slate-400 text-center leading-relaxed pt-2">
              By starting your trial, you agree to ProcureX's evaluation terms. Trial accounts are
              read-only — actions that require admin privileges (creating real records, deleting,
              sending notifications) are disabled.
            </p>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200 text-center text-sm">
            <span className="text-slate-500">Already have an account?</span>{' '}
            <Link to="/login" className="text-[#1E4E8C] hover:underline font-medium"
                  data-testid="trial-signin-link">
              Sign in
            </Link>
            <span className="text-slate-300 mx-2">·</span>
            <Link to="/sp10/login" className="text-[#1E4E8C] hover:underline font-medium"
                  data-testid="trial-portal-link">
              Supplier portal
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
